/**
 * LacedUpChicago — Header + Navigation
 *
 * Enhances the server-rendered Header (see sections/header.liquid) into a
 * focus-managed mobile drawer and keyboard-operable desktop dropdowns.
 * The underlying links/content already work without this script — see
 * assets/header.css for the no-JS fallback strategy.
 *
 * Theme Editor lifecycle:
 * Shopify replaces the entire [data-header] element's markup on a
 * section reload, so per-instance listeners bound inside initHeader()
 * are naturally discarded with the old DOM — no manual cleanup needed
 * there. Listeners bound to `document` are a different story: they
 * outlive any single header instance, so they are registered exactly
 * once (bindDocumentListeners, guarded by a flag) instead of inside
 * initHeader, which would otherwise accumulate a duplicate listener on
 * every reload.
 *
 * Drawer state can also go stale without a normal close: a Theme Editor
 * reload can remove the Header while its drawer is open, and resizing
 * (or rotating a device) can cross the 1000px breakpoint while the
 * drawer is open, CSS-hiding it without clearing its JS state. All of
 * these funnel through the single teardownDrawer() below so scroll-lock
 * and dialog semantics never get left behind.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';
  var SCROLL_LOCK_CLASS = 'header-scroll-lock';
  var activeHeader = null;
  var desktopMql = window.matchMedia('(min-width: 1000px)');

  function isConnected(el) {
    return !!el && document.body.contains(el);
  }

  // An element is only a real Tab-stop when it's actually rendered —
  // this excludes links/buttons inside a collapsed (display:none)
  // mobile submenu, which would otherwise be included in the focus
  // trap's first/last calculation despite being invisible/unreachable.
  function isRendered(el) {
    return el.offsetParent !== null;
  }

  function getFocusable(container) {
    var candidates = container.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(candidates, isRendered);
  }

  function trapFocus(container, event) {
    var focusable = getFocusable(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Clears a reference to a Header that Shopify has already removed
  // from the DOM (e.g. a Theme Editor reload while the drawer was
  // open), and reverses the one piece of global state a drawer can
  // leave behind: the scroll lock on <html>.
  function clearStaleActiveHeader() {
    if (activeHeader && !isConnected(activeHeader)) {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      activeHeader = null;
    }
  }

  function setSubmenuState(toggle, open) {
    var panelId = toggle.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.classList.toggle(OPEN_CLASS, open);

    var label = toggle.querySelector('[data-header-submenu-label]');
    if (label) {
      label.textContent = open ? toggle.dataset.labelOpen : toggle.dataset.labelClosed;
    }
  }

  function closeAllSubmenus(header, exceptToggle) {
    var toggles = header.querySelectorAll('[data-header-submenu-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      if (toggles[i] !== exceptToggle) setSubmenuState(toggles[i], false);
    }
  }

  function closeOpenDesktopSubmenu() {
    var openToggle = document.querySelector(
      '.header-nav-desktop [data-header-submenu-toggle][aria-expanded="true"]'
    );
    if (openToggle) setSubmenuState(openToggle, false);
    return openToggle;
  }

  function toggleSubmenu(toggle, header) {
    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    var isDesktopToggle = !!toggle.closest('.header-nav-desktop__item');

    // Desktop dropdowns are single-open (docs/features/header-navigation.md
    // §17); the mobile accordion allows independent, simultaneous sections.
    if (isDesktopToggle && !expanded) {
      closeAllSubmenus(header, toggle);
    }

    setSubmenuState(toggle, !expanded);
  }

  // Single shared cleanup path for every way the drawer can stop being
  // open: an explicit close, a stale/disconnected Header, or crossing
  // into the desktop breakpoint while it's open. Dialog semantics
  // (role/aria-modal) are applied only while the drawer is genuinely
  // acting as a JS-enhanced modal, and are removed here too.
  function teardownDrawer(header, options) {
    options = options || {};
    var drawer = header.querySelector('[data-header-drawer]');
    var trigger = header.querySelector('[data-header-drawer-open]');
    var panel = drawer && drawer.querySelector('[data-header-drawer-panel]');

    if (drawer) drawer.classList.remove(OPEN_CLASS);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
    }
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);

    if (activeHeader === header) activeHeader = null;
    if (options.returnFocus && trigger) trigger.focus();
  }

  function openDrawer(header) {
    var drawer = header.querySelector('[data-header-drawer]');
    var trigger = header.querySelector('[data-header-drawer-open]');
    var panel = drawer && drawer.querySelector('[data-header-drawer-panel]');
    if (!drawer || !trigger) return;

    drawer.classList.add(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'true');
    if (panel) {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
    }
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);

    activeHeader = header;

    var closeButton = drawer.querySelector('[data-header-drawer-close]');
    (closeButton || panel).focus();
  }

  function closeDrawer(header) {
    var drawer = header.querySelector('[data-header-drawer]');
    if (!drawer || !drawer.classList.contains(OPEN_CLASS)) return;
    teardownDrawer(header, { returnFocus: true });
  }

  function initHeader(header) {
    if (header.dataset.headerInitialized === 'true') return;
    header.dataset.headerInitialized = 'true';

    clearStaleActiveHeader();

    var openTrigger = header.querySelector('[data-header-drawer-open]');
    var closeTrigger = header.querySelector('[data-header-drawer-close]');
    var backdrop = header.querySelector('[data-header-drawer-backdrop]');

    if (openTrigger) {
      openTrigger.addEventListener('click', function () {
        openDrawer(header);
      });
    }

    if (closeTrigger) {
      closeTrigger.addEventListener('click', function () {
        closeDrawer(header);
      });
    }

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeDrawer(header);
      });
    }

    var toggles = header.querySelectorAll('[data-header-submenu-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      (function (toggle) {
        toggle.addEventListener('click', function () {
          toggleSubmenu(toggle, header);
        });
      })(toggles[i]);
    }
  }

  var documentListenersBound = false;

  function bindDocumentListeners() {
    if (documentListenersBound) return;
    documentListenersBound = true;

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      clearStaleActiveHeader();

      var openDrawerEl = document.querySelector('[data-header-drawer].is-open');
      if (openDrawerEl) {
        var header = openDrawerEl.closest('[data-header]');
        if (header) closeDrawer(header);
        return;
      }

      var openToggle = closeOpenDesktopSubmenu();
      if (openToggle) openToggle.focus();
    });

    // Keep keyboard focus trapped inside the open drawer.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      clearStaleActiveHeader();
      if (!activeHeader) return;

      var drawer = activeHeader.querySelector('[data-header-drawer].is-open');
      if (drawer) trapFocus(drawer, event);
    });

    // Defends the drawer focus trap against focus landing outside it
    // through means other than Tab (e.g. a screen reader virtual
    // cursor), and closes an open desktop submenu the moment focus
    // moves completely outside its owning nav item — independent of,
    // and in addition to, the Escape/outside-click handling below.
    document.addEventListener('focusin', function (event) {
      clearStaleActiveHeader();

      if (activeHeader) {
        var drawer = activeHeader.querySelector('[data-header-drawer].is-open');
        if (drawer && !drawer.contains(event.target)) {
          var focusable = getFocusable(drawer);
          if (focusable.length) focusable[0].focus();
        }
      }

      var openToggle = document.querySelector(
        '.header-nav-desktop [data-header-submenu-toggle][aria-expanded="true"]'
      );
      if (openToggle) {
        var item = openToggle.closest('.header-nav-desktop__item');
        if (item && !item.contains(event.target)) {
          setSubmenuState(openToggle, false);
        }
      }
    });

    // Closes an open desktop dropdown on outside click/tap.
    document.addEventListener('click', function (event) {
      var openToggle = document.querySelector(
        '.header-nav-desktop [data-header-submenu-toggle][aria-expanded="true"]'
      );
      if (!openToggle) return;

      var item = openToggle.closest('.header-nav-desktop__item');
      if (item && !item.contains(event.target)) {
        setSubmenuState(openToggle, false);
      }
    });

    // Crossing the desktop breakpoint while something is open leaves it
    // CSS-hidden but not torn down: closing the drawer when >=1000px is
    // reached prevents a lingering scroll-lock/focus-trap, and closing
    // an open desktop submenu when dropping back below 1000px prevents
    // stale expanded state in the (differently-structured) mobile
    // accordion's markup.
    function handleBreakpointChange(event) {
      clearStaleActiveHeader();

      if (event.matches) {
        if (activeHeader) teardownDrawer(activeHeader, { returnFocus: false });
      } else {
        closeOpenDesktopSubmenu();
      }
    }

    if (desktopMql.addEventListener) {
      desktopMql.addEventListener('change', handleBreakpointChange);
    } else if (desktopMql.addListener) {
      // Safari <14 fallback.
      desktopMql.addListener(handleBreakpointChange);
    }

    // Shopify fires this on a section's wrapper immediately before
    // removing it (Theme Editor reload/removal). Catching it here means
    // scroll-lock is cleared at the moment the Header actually
    // disappears, rather than waiting for the next stale-reference
    // check to notice.
    document.addEventListener('shopify:section:unload', function (event) {
      if (activeHeader && event.target && event.target.contains(activeHeader)) {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
        activeHeader = null;
      }
    });
  }
/*
 * HARDENED HEADER BOOTSTRAP
 *
 * The Header initializes itself directly instead of depending on theme.js.
 * This works:
 * - on normal storefront loads
 * - when header.js loads after DOMContentLoaded
 * - after Shopify Theme Editor section reloads
 */

function initHeaders(scope) {
  scope = scope || document;

  if (
    scope.matches &&
    scope.matches('[data-header]')
  ) {
    initHeader(scope);
  }

  if (!scope.querySelectorAll) return;

  var headers = scope.querySelectorAll('[data-header]');

  for (var i = 0; i < headers.length; i++) {
    initHeader(headers[i]);
  }
}

function bootHeaders() {
  initHeaders(document);
  bindDocumentListeners();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    bootHeaders,
    { once: true }
  );
} else {
  bootHeaders();
}

/*
 * Shopify replaces section markup without refreshing the page.
 * Reinitialize any new Header instance immediately.
 */
document.addEventListener('shopify:section:load', function (event) {
  initHeaders(event.target);
});
 
})();
